import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { autoTune, vuser } from '../../services/api';
import type { AgentConfiguratorState } from '../../types/agentConfigurator';
import type {
  CreatePipelineRequest,
  Pipeline,
  PipelineListItem,
  PipelineRun,
  PipelineScheduleType,
  PipelineTask,
} from '../../types/pipeline';
import type { OptimizationPolicyUiState } from '../../utils/optimizationPolicy';
import { formatPipelineErrorMessage, getErrorMessage, resolvePipelineRunError, resolvePipelineTaskRunError, extractPipelineTaskExperimentId } from '../../utils/errorUtils';
import ProgressBar from '../ui/ProgressBar';
import FailedJobErrorMessage from '../ui/FailedJobErrorMessage';
import {
  buildFullCyclePipelineRequest,
  buildTasksFromTemplate,
  formatPipelineTaskType,
  getTaskCatalogItem,
  isPipelineRunActive,
  canCancelPipelineRun,
  resolvePipelineRunDisplayTaskRuns,
  getEmptyPipelineRunTaskRunsMessage,
  normalizePipelineTaskType,
  normalizePipelineTasksForEditor,
  normalizePipelineUpdateBody,
  refreshPipelineTasksFromConfigurator,
  sortTasksByPipelineOrder,
  pipelineMatchesIntegration,
  pipelineStatusColor,
  PIPELINE_NAME_MAX_LEN,
  type BuildPipelineTaskContext,
  type PipelineTaskCatalogId,
} from '../../utils/pipelineDefaults';
import { scrollModalBodyToTarget } from '../../utils/modalFormValidation';
import {
  validatePipelineTaskFineTuning,
  validatePipelineTaskPeriodDays,
  validatePipelineTaskTrainingPeriod,
  validatePipelineTaskVirtualUsers,
  validatePipelineTasks,
} from '../../utils/pipelineTrainingPeriod';
import type { VirtualUser } from '../../types/vuser';
import type { ConnectorDataset } from '../../types/api';
import type { DatasetDataWarning } from '../../types/api';
import { connector } from '../../services/api';
import PipelineTaskConfigEditor from './PipelineTaskConfigEditor';
import PipelineToggleSwitch from './PipelineToggleSwitch';
import ConfirmModal from '../ConfirmModal';
import { NOTIFICATION_AUTO_HIDE_MS } from '../../constants/uiTiming';
import './PipelinePanel.css';

interface PipelinePanelProps {
  integrationId: string | null;
  configFromConfigurator?: AgentConfiguratorState | null;
  optimizationPolicyUi?: OptimizationPolicyUiState;
  onOptimizationPolicyUiChange?: (next: OptimizationPolicyUiState) => void;
  optimizationIterations?: number;
  onOptimizationIterationsChange?: (n: number) => void;
  onOpenExperiment?: (experimentId: string) => void;
}

const SCHEDULE_TYPES: PipelineScheduleType[] = ['hourly', 'daily', 'weekly', 'monthly'];
const PIPELINE_POLL_INTERVAL_MS = 8_000;
const OPTIMIZATION_JOB_POLL_INTERVAL_MS = 2_000;
const OPTIMIZATION_JOB_FAST_POLL_INTERVAL_MS = 1_000;
const OPTIMIZATION_JOB_INFER_SUCCEEDED_MS = 30_000;
const OPTIMIZATION_JOB_MAX_FINALIZING_MS = 120_000;

const TASK_TEMPLATES: { id: string; label: string; types: PipelineTaskCatalogId[] }[] = [
  {
    id: 'full',
    label: 'Full cycle',
    types: [
      'load_integration_datasets',
      'generate_vuser_datasets',
      'select_training_datasets',
      'config_tuning',
      'fine_tuning',
    ],
  },
  {
    id: 'datasets',
    label: 'Datasets only',
    types: ['load_integration_datasets', 'generate_vuser_datasets', 'select_training_datasets'],
  },
  {
    id: 'tuning',
    label: 'Tuning + FT',
    types: ['select_training_datasets', 'config_tuning', 'fine_tuning'],
  },
  {
    id: 'ft',
    label: 'Fine-tuning only',
    types: ['select_training_datasets', 'fine_tuning'],
  },
];

function resolvePipelineTemplateId(types: PipelineTaskCatalogId[]): string | null {
  const normalized = types.map((type) => normalizePipelineTaskType(type));
  for (const template of TASK_TEMPLATES) {
    if (
      template.types.length === normalized.length &&
      template.types.every((type, index) => normalizePipelineTaskType(type) === normalized[index])
    ) {
      return template.id;
    }
  }
  return null;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function normalizePipelineRun(run: PipelineRun, pipelineId?: string | null): PipelineRun {
  const resolvedPipelineId = run.pipeline_id || pipelineId || '';
  return resolvedPipelineId && run.pipeline_id === resolvedPipelineId
    ? run
    : { ...run, pipeline_id: resolvedPipelineId };
}

function pipelineRunMatchesListItem(pipeline: PipelineListItem, run: PipelineRun, pipelineIdFallback?: string | null): boolean {
  const runPipelineId = run.pipeline_id || pipelineIdFallback || '';
  return Boolean(
    (runPipelineId && pipeline.id === runPipelineId) || pipeline.last_run_id === run.id
  );
}

function pipelineMatchesActiveRun(
  pipeline: PipelineListItem,
  activeRun: PipelineRun | null,
  pipelineIdFallback?: string | null
): boolean {
  if (!activeRun) return false;
  return pipelineRunMatchesListItem(pipeline, activeRun, pipelineIdFallback);
}

function isPipelineRunPanelOpenForPipeline(
  pipeline: PipelineListItem,
  ctx: {
    showActiveRunPanel: boolean;
    viewedRun: PipelineRun | null;
    activeRun: PipelineRun | null;
    viewedRunPipelineId: string | null;
    activeRunPipelineId: string | null;
  }
): boolean {
  if (!ctx.showActiveRunPanel || !pipeline.last_run_id) return false;

  const panelRun = ctx.viewedRun ?? ctx.activeRun;
  if (!panelRun || panelRun.id !== pipeline.last_run_id) return false;

  if (ctx.viewedRunPipelineId) {
    return ctx.viewedRunPipelineId === pipeline.id;
  }

  return ctx.activeRunPipelineId === pipeline.id;
}

function resolvePipelineCardLastRunStatus(
  pipeline: PipelineListItem,
  activeRun: PipelineRun | null,
  pipelineIdFallback?: string | null
): PipelineListItem['last_status'] {
  if (pipelineMatchesActiveRun(pipeline, activeRun, pipelineIdFallback)) {
    return activeRun!.status;
  }
  return pipeline.last_status;
}

function resolvePipelineCardLastError(run: PipelineRun): string | null {
  if (isPipelineRunActive(run.status)) return null;

  const status = String(run.status || '').toLowerCase();
  if (status !== 'failed') return null;

  const resolved = resolvePipelineRunError(run);
  const message = (resolved.friendly || resolved.raw || run.error || '').trim();
  return message || null;
}

function syncPipelineListWithRun(
  pipelines: PipelineListItem[],
  run: PipelineRun,
  pipelineIdFallback?: string | null
): PipelineListItem[] {
  const normalizedRun = normalizePipelineRun(run, pipelineIdFallback);
  return pipelines.map((pipeline) =>
    pipelineRunMatchesListItem(pipeline, normalizedRun, pipelineIdFallback)
      ? {
          ...pipeline,
          last_run_id: normalizedRun.id,
          last_status: normalizedRun.status,
          last_error: resolvePipelineCardLastError(normalizedRun),
        }
      : pipeline
  );
}

function getPipelineTaskWarning(result: Record<string, unknown> | undefined): DatasetDataWarning | null {
  const warning = result?.warning;
  if (!warning || typeof warning !== 'object' || Array.isArray(warning)) return null;
  return warning as DatasetDataWarning;
}

function formatPipelineTaskWarningRequirement(warning: DatasetDataWarning): string | null {
  const parts = [
    warning.required_total != null ? `total ${warning.required_total}` : null,
    warning.required_train != null ? `train ${warning.required_train}` : null,
    warning.required_validation != null ? `validation ${warning.required_validation}` : null,
  ].filter(Boolean);
  return parts.length ? `Required: ${parts.join(', ')}` : null;
}

function pipelineResponseToListItem(
  pipeline: Pipeline,
  savedBody?: CreatePipelineRequest
): PipelineListItem {
  return {
    id: pipeline.id,
    name: savedBody?.name.trim() || pipeline.name,
    enabled: savedBody?.enabled ?? pipeline.enabled,
    schedule_type: savedBody?.schedule_type ?? pipeline.schedule_type ?? null,
    schedule_config: pipeline.schedule_config ?? null,
    tasks: savedBody?.tasks ?? pipeline.tasks,
    next_run_at: pipeline.next_run_at ?? null,
    last_run_at: pipeline.last_run_at ?? null,
    last_run_id: pipeline.last_run_id ?? null,
    last_status: pipeline.last_status ?? null,
    last_error: pipeline.last_error ?? null,
  };
}

function upsertPipelineListItem(
  pipelines: PipelineListItem[],
  item: PipelineListItem
): PipelineListItem[] {
  const index = pipelines.findIndex((pipeline) => pipeline.id === item.id);
  if (index === -1) return [item, ...pipelines];

  const existing = pipelines[index];
  return pipelines.map((pipeline, pipelineIndex) =>
    pipelineIndex === index
      ? {
          ...existing,
          ...item,
          last_run_id: item.last_run_id ?? existing.last_run_id,
          last_status: item.last_status ?? existing.last_status,
          last_run_at: item.last_run_at ?? existing.last_run_at,
          next_run_at: item.next_run_at ?? existing.next_run_at,
          last_error: item.last_error ?? existing.last_error,
        }
      : pipeline
  );
}

function applyPipelineListItemUpdate(
  pipelines: PipelineListItem[],
  item: PipelineListItem,
  activeRunSnapshot: PipelineRun | null,
  activeRunPipelineId?: string | null
): PipelineListItem[] {
  const updated = upsertPipelineListItem(pipelines, item);
  return activeRunSnapshot
    ? syncPipelineListWithRun(updated, activeRunSnapshot, activeRunPipelineId)
    : updated;
}

const ACTIVE_PIPELINE_RUN_STORAGE_PREFIX = 'connector-pipeline-active-run:';

interface StoredActivePipelineRun {
  pipelineId: string;
  runId: string;
  pipelineName: string;
}

function readStoredActivePipelineRun(integrationId: string): StoredActivePipelineRun | null {
  try {
    const raw = sessionStorage.getItem(`${ACTIVE_PIPELINE_RUN_STORAGE_PREFIX}${integrationId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredActivePipelineRun;
    if (!parsed.pipelineId || !parsed.runId) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredActivePipelineRun(
  integrationId: string,
  value: StoredActivePipelineRun | null
): void {
  const key = `${ACTIVE_PIPELINE_RUN_STORAGE_PREFIX}${integrationId}`;
  if (!value) {
    sessionStorage.removeItem(key);
    return;
  }
  sessionStorage.setItem(key, JSON.stringify(value));
}

function resolvePipelineTasks(full: Pipeline, fallback?: PipelineListItem): PipelineTask[] {
  const raw =
    Array.isArray(full.tasks) && full.tasks.length > 0
      ? full.tasks
      : fallback && Array.isArray(fallback.tasks) && fallback.tasks.length > 0
        ? fallback.tasks
        : [];
  return normalizePipelineTasksForEditor(raw);
}

const PipelinePanel: React.FC<PipelinePanelProps> = ({
  integrationId,
  configFromConfigurator = null,
  optimizationPolicyUi = { agents: {}, metaPrompts: {} },
  onOptimizationPolicyUiChange = () => undefined,
  optimizationIterations = 4,
  onOptimizationIterationsChange = () => undefined,
  onOpenExperiment,
}) => {
  const [pipelines, setPipelines] = useState<PipelineListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [editingPipelineId, setEditingPipelineId] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [togglingEnabledId, setTogglingEnabledId] = useState<string | null>(null);
  const [runningPipelineId, setRunningPipelineId] = useState<string | null>(null);
  const [cancellingRunId, setCancellingRunId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; pipelineId: string | null }>({
    isOpen: false,
    pipelineId: null,
  });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [activeRun, setActiveRun] = useState<PipelineRun | null>(null);
  const [activeRunPipelineName, setActiveRunPipelineName] = useState('');
  const [viewedRun, setViewedRun] = useState<PipelineRun | null>(null);
  const [viewedRunPipelineId, setViewedRunPipelineId] = useState<string | null>(null);
  const [viewedRunPipelineName, setViewedRunPipelineName] = useState('');
  const [showActiveRunPanel, setShowActiveRunPanel] = useState(false);
  const [loadingViewRunPipelineId, setLoadingViewRunPipelineId] = useState<string | null>(null);
  const [virtualUsers, setVirtualUsers] = useState<VirtualUser[]>([]);
  const [connectorDatasets, setConnectorDatasets] = useState<ConnectorDataset[]>([]);
  const [connectorDatasetsLoading, setConnectorDatasetsLoading] = useState(false);
  const isPollingRef = useRef(false);
  const cancellingRunIdRef = useRef<string | null>(null);
  const activeRunRef = useRef<PipelineRun | null>(null);
  const activeRunPipelineIdRef = useRef<string | null>(null);
  const modalBodyRef = useRef<HTMLDivElement | null>(null);
  const runPanelRef = useRef<HTMLElement | null>(null);

  const [form, setForm] = useState<CreatePipelineRequest>({
    name: 'Full AI Tuner cycle',
    enabled: true,
    schedule_type: 'daily',
    tasks: [],
  });

  const taskContext = useMemo<BuildPipelineTaskContext | null>(() => {
    if (!integrationId || !configFromConfigurator) return null;
    return {
      integrationId,
      config: configFromConfigurator,
      optimizationPolicyUi,
      virtualUserIds: virtualUsers.filter((u) => u.type === 'user').map((u) => u.id),
      virtualAdminIds: virtualUsers.filter((u) => u.type === 'admin').map((u) => u.id),
      iterations: optimizationIterations,
    };
  }, [integrationId, configFromConfigurator, optimizationPolicyUi, virtualUsers, optimizationIterations]);

  const hasSelectTrainingInForm = useMemo(
    () =>
      form.tasks.some(
        (t) => t.enabled && normalizePipelineTaskType(t.type) === 'select_training_datasets'
      ),
    [form.tasks]
  );

  const filteredPipelines = useMemo(
    () =>
      integrationId
        ? pipelines.filter((p) => pipelineMatchesIntegration(p, integrationId))
        : [],
    [pipelines, integrationId]
  );

  const clearSuccess = () => setTimeout(() => setSuccess(null), NOTIFICATION_AUTO_HIDE_MS);

  const loadPipelines = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const resp = await autoTune.listPipelines({ limit: 100, offset: 0 });
      const items = resp?.data?.items;
      const nextItems = Array.isArray(items) ? items : [];
      setPipelines(() => {
        const snapshot = activeRunRef.current;
        const pipelineId = activeRunPipelineIdRef.current;
        return snapshot
          ? syncPipelineListWithRun(nextItems, snapshot, pipelineId)
          : nextItems;
      });
    } catch (e: unknown) {
      if (!silent) {
        setError(formatPipelineErrorMessage(getErrorMessage(e, 'Failed to load pipelines'), 'Failed to load pipelines'));
      }
      setPipelines([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const restoreActivePipelineRun = useCallback(async () => {
    if (!integrationId) return;
    if (activeRunRef.current && isPipelineRunActive(activeRunRef.current.status)) {
      return;
    }

    const stored = readStoredActivePipelineRun(integrationId);
    if (!stored) return;

    try {
      const resp = await autoTune.getPipelineRun(stored.runId);
      if (!resp.success || !resp.data) {
        writeStoredActivePipelineRun(integrationId, null);
        return;
      }

      const normalizedRun = normalizePipelineRun(resp.data, stored.pipelineId);
      if (!isPipelineRunActive(normalizedRun.status)) {
        writeStoredActivePipelineRun(integrationId, null);
        setPipelines((prev) => syncPipelineListWithRun(prev, normalizedRun, stored.pipelineId));
        return;
      }

      activeRunPipelineIdRef.current = stored.pipelineId;
      setActiveRunPipelineName(stored.pipelineName);
      setActiveRun(normalizedRun);
      setPipelines((prev) => syncPipelineListWithRun(prev, normalizedRun, stored.pipelineId));
    } catch {
      writeStoredActivePipelineRun(integrationId, null);
    }
  }, [integrationId]);

  const loadVirtualUsers = useCallback(async () => {
    if (!integrationId) {
      setVirtualUsers([]);
      return;
    }
    try {
      const resp = await vuser.listVirtualUsers(integrationId);
      setVirtualUsers(Array.isArray(resp?.data) ? resp.data : []);
    } catch {
      setVirtualUsers([]);
    }
  }, [integrationId]);

  const loadConnectorDatasets = useCallback(async () => {
    if (!integrationId) {
      setConnectorDatasets([]);
      return;
    }
    setConnectorDatasetsLoading(true);
    try {
      const resp = await connector.getDatasets(integrationId);
      const items = resp?.data;
      setConnectorDatasets(Array.isArray(items) ? items : []);
    } catch {
      setConnectorDatasets([]);
    } finally {
      setConnectorDatasetsLoading(false);
    }
  }, [integrationId]);

  useEffect(() => {
    setActiveRun(null);
    setViewedRun(null);
    setViewedRunPipelineId(null);
    setViewedRunPipelineName('');
    setShowActiveRunPanel(false);
    activeRunPipelineIdRef.current = null;
    setActiveRunPipelineName('');
    void (async () => {
      await loadPipelines();
      await restoreActivePipelineRun();
    })();
    void loadVirtualUsers();
  }, [integrationId, loadPipelines, loadVirtualUsers, restoreActivePipelineRun]);

  useEffect(() => {
    if (showForm && integrationId) void loadConnectorDatasets();
  }, [showForm, integrationId, loadConnectorDatasets]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (document.hidden) return;
      if (isPollingRef.current) return;
      isPollingRef.current = true;
      void loadPipelines({ silent: true }).finally(() => {
        isPollingRef.current = false;
      });
    }, PIPELINE_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [loadPipelines]);

  useEffect(() => {
    activeRunRef.current = activeRun;
  }, [activeRun]);

  useEffect(() => {
    if (!integrationId || !activeRun) return;
    const pipelineId = activeRun.pipeline_id || activeRunPipelineIdRef.current;
    if (pipelineId && isPipelineRunActive(activeRun.status)) {
      writeStoredActivePipelineRun(integrationId, {
        pipelineId,
        runId: activeRun.id,
        pipelineName: activeRunPipelineName || 'Pipeline run',
      });
      return;
    }
    if (!isPipelineRunActive(activeRun.status)) {
      writeStoredActivePipelineRun(integrationId, null);
    }
  }, [activeRun, activeRunPipelineName, integrationId]);

  useEffect(() => {
    if (!activeRun) return;

    const runId = activeRun.id;
    const pipelineId = activeRun.pipeline_id || activeRunPipelineIdRef.current || undefined;

    const applyRunUpdate = (run: PipelineRun) => {
      const normalizedRun = normalizePipelineRun(run, pipelineId);
      setActiveRun(normalizedRun);
      setPipelines((prev) => syncPipelineListWithRun(prev, normalizedRun, pipelineId));
    };

    if (!isPipelineRunActive(activeRun.status)) {
      applyRunUpdate(activeRun);
      return;
    }

    const pollRun = () => {
      void autoTune.getPipelineRun(runId).then((resp) => {
        if (resp.success && resp.data) {
          applyRunUpdate(resp.data);
        }
      });
    };

    pollRun();
    const intervalId = window.setInterval(pollRun, PIPELINE_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [activeRun?.id, activeRun?.status]);

  const openCreateForm = (templateTypes?: PipelineTaskCatalogId[]) => {
    if (!taskContext) {
      setError('Select an integration and load its configurator before creating a pipeline.');
      return;
    }

    const types = templateTypes ?? [
      'load_integration_datasets',
      'generate_vuser_datasets',
      'select_training_datasets',
      'config_tuning',
      'fine_tuning',
    ];
    const payload = buildFullCyclePipelineRequest({ ...taskContext, scheduleType: 'daily' });
    setForm({
      ...payload,
      tasks: normalizePipelineTasksForEditor(buildTasksFromTemplate(types, taskContext)),
    });

    setSelectedTemplateId(resolvePipelineTemplateId(types));
    setEditingPipelineId(null);
    setShowForm(true);
    setError(null);
  };

  const openBlankForm = () => {
    if (!taskContext) {
      setError('Select an integration and load its configurator before creating a pipeline.');
      return;
    }
    setForm({
      name: 'New pipeline',
      enabled: true,
      schedule_type: 'daily',
      tasks: [],
    });
    setSelectedTemplateId(null);
    setEditingPipelineId(null);
    setShowForm(true);
    setError(null);
  };

  const openEditForm = async (pipelineId: string) => {
    setFormLoading(true);
    setError(null);
    try {
      const resp = await autoTune.getPipeline(pipelineId);
      if (!resp.success || !resp.data) {
        throw new Error(resp.error?.message || 'Failed to load pipeline');
      }
      const pipeline = resp.data;
      const listItem = pipelines.find((p) => p.id === pipelineId);
      const tasks = resolvePipelineTasks(pipeline, listItem);
      if (tasks.length === 0) {
        throw new Error('Pipeline tasks are missing. Recreate the pipeline or contact support.');
      }
      setForm({
        name: pipeline.name,
        enabled: Boolean(pipeline.enabled),
        schedule_type: pipeline.schedule_type ?? null,
        tasks: sortTasksByPipelineOrder(tasks),
      });
      setSelectedTemplateId(
        resolvePipelineTemplateId(tasks.map((task) => normalizePipelineTaskType(task.type) as PipelineTaskCatalogId))
      );
      setEditingPipelineId(pipelineId);
      setShowForm(true);
    } catch (e: unknown) {
      setError(formatPipelineErrorMessage(getErrorMessage(e, 'Failed to load pipeline'), 'Failed to load pipeline'));
    } finally {
      setFormLoading(false);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingPipelineId(null);
    setSelectedTemplateId(null);
    setError(null);
  };

  const scrollToFirstPipelineTaskValidationIssue = (tasks: PipelineTask[]) => {
    const modalBody = modalBodyRef.current;
    window.requestAnimationFrame(() => {
      const inlineError = modalBody?.querySelector('.pipeline-task-config__inline-error');
      if (inlineError instanceof HTMLElement) {
        inlineError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      for (let i = 0; i < tasks.length; i += 1) {
        const task = tasks[i];
        if (!task.enabled) continue;
        const type = normalizePipelineTaskType(task.type);
        const config = task.config as Record<string, unknown>;
        const hasIssue =
          validatePipelineTaskTrainingPeriod(type, config) ||
          validatePipelineTaskPeriodDays(type, config) ||
          validatePipelineTaskVirtualUsers(type, config, virtualUsers) ||
          validatePipelineTaskFineTuning(type, config);
        if (!hasIssue) continue;
        const card = modalBody?.querySelector(`[data-pipeline-task-index="${i}"]`);
        if (card instanceof HTMLElement) {
          card.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          scrollModalBodyToTarget(modalBody, { scrollTop: true });
        }
        return;
      }
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Pipeline name is required.');
      return;
    }
    if (!form.tasks.length) {
      setError('Add at least one task to the pipeline.');
      return;
    }
    if (!form.tasks.some((task) => task.enabled)) {
      setError('Enable at least one task.');
      return;
    }

    let tasks = form.tasks;
    if (taskContext) {
      tasks = refreshPipelineTasksFromConfigurator(tasks, taskContext);
    }

    const validationError = validatePipelineTasks(tasks, { virtualUsers });
    if (validationError) {
      setError(validationError);
      scrollToFirstPipelineTaskValidationIssue(tasks);
      return;
    }

    const body = normalizePipelineUpdateBody({ ...form, tasks }, integrationId ?? undefined);

    setFormLoading(true);
    setError(null);
    let savedListItem: PipelineListItem | null = null;
    try {
      if (editingPipelineId) {
        const resp = await autoTune.updatePipeline(editingPipelineId, body);
        if (!resp.success || !resp.data) {
          throw new Error(resp.error?.message || 'Failed to update pipeline');
        }
        savedListItem = pipelineResponseToListItem(resp.data, body);
        setSuccess('Pipeline updated');
      } else {
        const resp = await autoTune.createPipeline(body);
        if (!resp.success || !resp.data) {
          throw new Error(resp.error?.message || 'Failed to create pipeline');
        }
        savedListItem = pipelineResponseToListItem(resp.data, body);
        setSuccess('Pipeline created');
      }

      if (savedListItem) {
        setPipelines((prev) =>
          applyPipelineListItemUpdate(prev, savedListItem!, activeRunRef.current, activeRunPipelineIdRef.current)
        );
        if (activeRunPipelineIdRef.current === savedListItem.id) {
          setActiveRunPipelineName(savedListItem.name);
        }
      }

      clearSuccess();
      closeForm();
      try {
        await loadPipelines({ silent: true });
      } finally {
        if (savedListItem) {
          setPipelines((prev) =>
            applyPipelineListItemUpdate(prev, savedListItem!, activeRunRef.current, activeRunPipelineIdRef.current)
          );
          if (activeRunPipelineIdRef.current === savedListItem.id) {
            setActiveRunPipelineName(savedListItem.name);
          }
        }
      }
    } catch (e: unknown) {
      setError(
        formatPipelineErrorMessage(
          getErrorMessage(e, editingPipelineId ? 'Failed to update pipeline' : 'Failed to create pipeline'),
          editingPipelineId ? 'Failed to update pipeline' : 'Failed to create pipeline'
        )
      );
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleEnabled = async (pipeline: PipelineListItem) => {
    const nextEnabled = !Boolean(pipeline.enabled);
    setTogglingEnabledId(pipeline.id);
    setError(null);

    setPipelines((prev) =>
      prev.map((item) => (item.id === pipeline.id ? { ...item, enabled: nextEnabled } : item))
    );

    try {
      const full = await autoTune.getPipeline(pipeline.id);
      if (!full.success || !full.data) {
        throw new Error(full.error?.message || 'Failed to load pipeline');
      }
      const tasks = resolvePipelineTasks(full.data, pipeline);
      if (tasks.length === 0) {
        throw new Error('Cannot update pipeline: tasks are missing on the server.');
      }

      const body = normalizePipelineUpdateBody(
        {
          name: full.data.name,
          enabled: nextEnabled,
          schedule_type: full.data.schedule_type ?? null,
          tasks: sortTasksByPipelineOrder(tasks),
        },
        integrationId ?? undefined
      );

      const resp = await autoTune.updatePipeline(pipeline.id, body);
      if (!resp.success) throw new Error(resp.error?.message || 'Failed to update pipeline');

      setSuccess(nextEnabled ? 'Pipeline enabled' : 'Pipeline disabled');
      clearSuccess();
      await loadPipelines();
    } catch (e: unknown) {
      setPipelines((prev) =>
        prev.map((item) => (item.id === pipeline.id ? { ...item, enabled: !nextEnabled } : item))
      );
      setError(formatPipelineErrorMessage(getErrorMessage(e, 'Failed to update pipeline'), 'Failed to update pipeline'));
    } finally {
      setTogglingEnabledId(null);
    }
  };

  const handleRun = async (pipeline: PipelineListItem) => {
    setRunningPipelineId(pipeline.id);
    setError(null);
    try {
      const full = await autoTune.getPipeline(pipeline.id);
      if (!full.success || !full.data) {
        throw new Error(full.error?.message || 'Failed to load pipeline');
      }
      const tasks = resolvePipelineTasks(full.data, pipeline);
      const validationError = validatePipelineTasks(tasks, { virtualUsers });
      if (validationError) {
        setError(`${validationError} Edit the pipeline and fix the configuration before running.`);
        return;
      }

      const resp = await autoTune.startPipelineRun(pipeline.id);
      if (!resp.success || !resp.data) {
        throw new Error(resp.error?.message || 'Failed to start pipeline run');
      }
      setViewedRun(null);
      setViewedRunPipelineId(null);
      setViewedRunPipelineName('');
      activeRunPipelineIdRef.current = pipeline.id;
      const normalizedRun = normalizePipelineRun(resp.data, pipeline.id);
      setActiveRun(normalizedRun);
      setActiveRunPipelineName(pipeline.name);
      setShowActiveRunPanel(true);
      setPipelines((prev) => syncPipelineListWithRun(prev, normalizedRun, pipeline.id));
      setSuccess('Pipeline run started');
      clearSuccess();
      await loadPipelines({ silent: true });
      if (activeRunRef.current) {
        setPipelines((prev) =>
          syncPipelineListWithRun(prev, activeRunRef.current!, activeRunPipelineIdRef.current)
        );
      }
    } catch (e: unknown) {
      setError(formatPipelineErrorMessage(getErrorMessage(e, 'Failed to start pipeline run'), 'Failed to start pipeline run'));
    } finally {
      setRunningPipelineId(null);
    }
  };

  const handleViewRun = async (pipeline: PipelineListItem) => {
    if (!pipeline.last_run_id || loadingViewRunPipelineId) return;

    if (
      isPipelineRunPanelOpenForPipeline(pipeline, {
        showActiveRunPanel,
        viewedRun,
        activeRun,
        viewedRunPipelineId,
        activeRunPipelineId: activeRunPipelineIdRef.current,
      })
    ) {
      closeRunPanel();
      return;
    }

    setError(null);
    setLoadingViewRunPipelineId(pipeline.id);
    try {
      const resp = await autoTune.getPipelineRun(pipeline.last_run_id);
      if (!resp.success || !resp.data) {
        throw new Error(resp.error?.message || 'Failed to load run status');
      }
      const normalizedRun = normalizePipelineRun(resp.data, pipeline.id);
      const liveRun = activeRunRef.current;
      const livePipelineId = activeRunPipelineIdRef.current;
      const isSameAsLiveRun =
        Boolean(liveRun) &&
        liveRun!.id === normalizedRun.id &&
        livePipelineId === pipeline.id &&
        isPipelineRunActive(normalizedRun.status);

      if (isSameAsLiveRun) {
        setActiveRun(normalizedRun);
        setViewedRun(null);
        setViewedRunPipelineId(null);
        setViewedRunPipelineName('');
      } else {
        setViewedRun(normalizedRun);
        setViewedRunPipelineId(pipeline.id);
        setViewedRunPipelineName(pipeline.name);
      }

      setShowActiveRunPanel(true);
      setPipelines((prev) => syncPipelineListWithRun(prev, normalizedRun, pipeline.id));
      requestAnimationFrame(() => {
        runPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    } catch (e: unknown) {
      setError(formatPipelineErrorMessage(getErrorMessage(e, 'Failed to load run status'), 'Failed to load run status'));
    } finally {
      setLoadingViewRunPipelineId(null);
    }
  };

  const closeRunPanel = () => {
    setShowActiveRunPanel(false);
    setViewedRun(null);
    setViewedRunPipelineId(null);
    setViewedRunPipelineName('');
  };

  const handleCancelRun = async (runId: string) => {
    if (cancellingRunIdRef.current) return;

    cancellingRunIdRef.current = runId;
    setCancellingRunId(runId);
    setError(null);
    try {
      const resp = await autoTune.cancelPipelineRun(runId);
      if (!resp.success || !resp.data) {
        throw new Error(resp.error?.message || 'Failed to cancel run');
      }
      const normalizedRun = normalizePipelineRun(resp.data, activeRunPipelineIdRef.current);
      setActiveRun((prev) => (prev?.id === runId ? normalizedRun : prev));
      setViewedRun((prev) => (prev?.id === runId ? normalizedRun : prev));
      setPipelines((prev) => syncPipelineListWithRun(prev, normalizedRun, activeRunPipelineIdRef.current));
      setSuccess('Cancellation requested');
      clearSuccess();
      await loadPipelines({ silent: true });
    } catch (e: unknown) {
      setError(formatPipelineErrorMessage(getErrorMessage(e, 'Failed to cancel run'), 'Failed to cancel run'));
    } finally {
      cancellingRunIdRef.current = null;
      setCancellingRunId(null);
    }
  };

  const confirmDelete = async () => {
    const pipelineId = deleteConfirm.pipelineId;
    if (!pipelineId) return;
    setDeleteLoading(true);
    try {
      const resp = await autoTune.deletePipeline(pipelineId);
      if (!resp.success) throw new Error(resp.error?.message || 'Failed to delete pipeline');
      setDeleteConfirm({ isOpen: false, pipelineId: null });
      if (activeRunPipelineIdRef.current === pipelineId) {
        setActiveRun(null);
        closeRunPanel();
        activeRunPipelineIdRef.current = null;
        if (integrationId) writeStoredActivePipelineRun(integrationId, null);
      } else if (viewedRunPipelineId === pipelineId) {
        closeRunPanel();
      }
      setSuccess('Pipeline deleted');
      clearSuccess();
      await loadPipelines();
    } catch (e: unknown) {
      setError(formatPipelineErrorMessage(getErrorMessage(e, 'Failed to delete pipeline'), 'Failed to delete pipeline'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const applyTemplate = (templateId: string, types: PipelineTaskCatalogId[]) => {
    if (!taskContext) return;
    setSelectedTemplateId(templateId);
    setForm((prev) => ({
      ...prev,
      tasks: normalizePipelineTasksForEditor(buildTasksFromTemplate(types, taskContext, prev.tasks)),
    }));
  };

  const updateTask = (index: number, next: PipelineTask) => {
    setForm((prev) => ({
      ...prev,
      tasks: prev.tasks.map((t, i) => (i === index ? next : t)),
    }));
  };

  const renderEntityLink = (entityType?: string, entityId?: string) => {
    if (!entityType || !entityId) return null;
    return (
      <button
        type="button"
        className="pipeline-entity-link"
        title={entityId}
        onClick={() => {
          void navigator.clipboard.writeText(entityId);
          setSuccess('Entity ID copied');
          clearSuccess();
        }}
      >
        <i className="fas fa-link" aria-hidden="true" />
        {entityType.replace(/_/g, ' ')} · {entityId.slice(0, 8)}…
      </button>
    );
  };

  const renderExperimentLink = (taskRun: { type?: string; result?: Record<string, unknown> }) => {
    const experimentId = extractPipelineTaskExperimentId(taskRun);
    if (!experimentId) return null;
    return (
      <button
        type="button"
        className="pipeline-entity-link pipeline-entity-link--experiment"
        title={experimentId}
        onClick={() => {
          if (onOpenExperiment) {
            onOpenExperiment(experimentId);
            return;
          }
          void navigator.clipboard.writeText(experimentId);
          setSuccess('Experiment ID copied');
          clearSuccess();
        }}
      >
        <i className="fas fa-flask" aria-hidden="true" />
        View experiment · {experimentId.slice(0, 8)}…
      </button>
    );
  };

  const panelRun = viewedRun ?? activeRun;
  const panelRunName = viewedRun ? viewedRunPipelineName : activeRunPipelineName;
  const panelPipelineId = viewedRunPipelineId ?? panelRun?.pipeline_id ?? null;
  const panelPipeline = panelPipelineId
    ? pipelines.find((pipeline) => pipeline.id === panelPipelineId)
    : undefined;
  const panelTaskRuns = panelRun
    ? resolvePipelineRunDisplayTaskRuns(panelRun, panelPipeline?.tasks)
    : [];
  const panelIsLiveActiveRun =
    Boolean(activeRun) &&
    panelRun?.id === activeRun?.id &&
    isPipelineRunActive(activeRun?.status);
  const panelEyebrow = panelIsLiveActiveRun ? 'Active run' : `Last run · ${panelRun?.status ?? 'unknown'}`;

  if (!integrationId) {
    return null;
  }

  return (
    <div className="pipeline-panel" id="pipeline-panel-root">
      <div className="pipeline-panel__toolbar" id="pipeline-panel-toolbar">
        <div className="pipeline-panel__toolbar-copy">
          <p className="pipeline-panel__toolbar-hint">Build flows, schedule runs, and monitor pipeline progress.</p>
        </div>
        <div className="pipeline-panel__toolbar-actions">
          <button type="button" className="pipeline-btn pipeline-btn--ghost" onClick={() => openCreateForm()}>
            <i className="fas fa-layer-group" aria-hidden="true" /> Full cycle
          </button>
          <button type="button" className="pipeline-btn pipeline-btn--primary" onClick={() => openBlankForm()}>
            <i className="fas fa-plus" aria-hidden="true" /> New pipeline
          </button>
        </div>
      </div>

      {(error || success) && !showForm && (
        <div className="pipeline-toasts">
          {error && (
            <div className="pipeline-toast pipeline-toast--error" role="alert">
              <i className="fas fa-exclamation-circle" aria-hidden="true" />
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)} aria-label="Dismiss">×</button>
            </div>
          )}
          {success && (
            <div className="pipeline-toast pipeline-toast--success" role="status">
              <i className="fas fa-check-circle" aria-hidden="true" />
              <span>{success}</span>
              <button type="button" onClick={() => setSuccess(null)} aria-label="Dismiss">×</button>
            </div>
          )}
        </div>
      )}

      {panelRun && showActiveRunPanel && (
        <section
          ref={runPanelRef}
          id="pipeline-active-run-panel"
          className="pipeline-run-panel pipeline-run-panel--open"
        >
          <div className="pipeline-run-panel__head">
            <div>
              <span className="pipeline-run-panel__eyebrow">{panelEyebrow}</span>
              <h3 className="pipeline-run-panel__title" title={panelRunName || undefined}>
                {panelRunName || 'Pipeline run'}
              </h3>
              {!panelIsLiveActiveRun ? (
                <p className="pipeline-run-panel__hint">
                  Steps, progress, and errors for this pipeline&apos;s last run.
                </p>
              ) : null}
              <code className="pipeline-run-panel__id">{panelRun.id}</code>
            </div>
            <div className="pipeline-run-panel__actions">
              {panelIsLiveActiveRun && canCancelPipelineRun(panelRun.status) && (
                <button
                  type="button"
                  className="pipeline-btn pipeline-btn--danger"
                  disabled={Boolean(cancellingRunId)}
                  aria-busy={cancellingRunId === panelRun.id}
                  onClick={() => void handleCancelRun(panelRun.id)}
                >
                  <i
                    className={`fas ${cancellingRunId === panelRun.id ? 'fa-spinner fa-spin' : 'fa-ban'}`}
                    aria-hidden="true"
                  />
                  {cancellingRunId === panelRun.id ? 'Cancelling…' : 'Cancel run'}
                </button>
              )}
              <button type="button" className="pipeline-icon-btn" onClick={closeRunPanel} aria-label="Close">
                ×
              </button>
            </div>
          </div>

          <div className="pipeline-run-panel__progress">
            <div className="pipeline-run-panel__progress-meta">
              <span className={`pipeline-status-badge pipeline-status-badge--${panelRun.status}`}>
                {panelRun.status}
              </span>
              <span>{panelRun.progress_percent ?? 0}% complete</span>
              <span>Step {(panelRun.current_task_index ?? 0) + 1}</span>
            </div>
            <ProgressBar
              percent={Math.max(0, Math.min(100, panelRun.progress_percent ?? 0))}
              variant={
                panelRun.status === 'succeeded'
                  ? 'completed'
                  : panelRun.status === 'partially_succeeded'
                    ? 'warning'
                  : panelRun.status === 'failed'
                    ? 'failed'
                    : 'running'
              }
            />
          </div>

          {panelRun.status === 'failed' ? (
            (() => {
              const runError = resolvePipelineRunError(panelRun);
              return (
                <FailedJobErrorMessage
                  friendlyMessage={runError.friendly}
                  rawError={runError.raw}
                  details={runError.details}
                  fallbackMessage="Pipeline run failed"
                  compact
                />
              );
            })()
          ) : null}

          {panelRun.status === 'partially_succeeded' && panelRun.error ? (
            <div className="pipeline-run-panel__warning" role="status">
              <i className="fas fa-exclamation-triangle" aria-hidden="true" />
              <span>{formatPipelineErrorMessage(panelRun.error, 'Some pipeline steps failed, but the run continued.')}</span>
            </div>
          ) : null}

          <div className="pipeline-run-steps">
            {panelTaskRuns.length === 0 ? (
              <p className="pipeline-muted">{getEmptyPipelineRunTaskRunsMessage(panelRun.status)}</p>
            ) : (
              panelTaskRuns.map((taskRun, index) => {
                const catalog = getTaskCatalogItem(taskRun.type);
                const taskWarning = getPipelineTaskWarning(taskRun.result);
                return (
                  <div key={`${taskRun.type}-${index}`} className={`pipeline-run-step pipeline-run-step--${taskRun.status}`}>
                    <div className="pipeline-run-step__icon" style={{ color: catalog?.accent || '#5B7893' }}>
                      <i className={`fas ${catalog?.icon || 'fa-circle'}`} aria-hidden="true" />
                    </div>
                    <div className="pipeline-run-step__body">
                      <div className="pipeline-run-step__head">
                        <strong>{formatPipelineTaskType(taskRun.type)}</strong>
                        <span style={{ color: pipelineStatusColor(taskRun.status) }}>{taskRun.status}</span>
                        {typeof taskRun.progress_percent === 'number' && (
                          <span className="pipeline-muted">{taskRun.progress_percent}%</span>
                        )}
                      </div>
                      {renderEntityLink(taskRun.entity_type, taskRun.entity_id)}
                      {renderExperimentLink(taskRun)}
                      {taskWarning ? (
                        <div className="pipeline-run-step__warning" role="status">
                          <i className="fas fa-exclamation-triangle" aria-hidden="true" />
                          <div>
                            <strong>{taskWarning.message || 'Dataset was created, but may be too small for tuning.'}</strong>
                            {formatPipelineTaskWarningRequirement(taskWarning) ? (
                              <span>{formatPipelineTaskWarningRequirement(taskWarning)}</span>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                      {taskRun.status === 'failed' ? (
                        (() => {
                          const err = resolvePipelineTaskRunError(taskRun);
                          return (
                            <FailedJobErrorMessage
                              friendlyMessage={err.friendly}
                              rawError={err.raw}
                              details={err.details}
                              fallbackMessage="Task failed"
                              compact
                            />
                          );
                        })()
                      ) : null}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>
      )}

      {showForm && (
        <div className="pipeline-modal-backdrop">
          <div className="pipeline-modal" role="dialog" aria-modal="true">
            <div className="pipeline-modal__head">
              <div>
                <span className="pipeline-modal__eyebrow">{editingPipelineId ? 'Edit' : 'Create'}</span>
                <h3>{editingPipelineId ? 'Update pipeline' : 'New pipeline'}</h3>
              </div>
              <button type="button" className="pipeline-icon-btn" onClick={closeForm} aria-label="Close">
                ×
              </button>
            </div>

            <div className="pipeline-modal__body" ref={modalBodyRef}>
              {error ? (
                <div className="pipeline-modal__alert pipeline-modal__alert--error" role="alert">
                  <i className="fas fa-exclamation-circle" aria-hidden="true" />
                  <span>{error}</span>
                  <button type="button" onClick={() => setError(null)} aria-label="Dismiss">
                    ×
                  </button>
                </div>
              ) : null}

              <section className="pipeline-form-section">
                <h4>General</h4>
                <div className="pipeline-form-grid">
                  <label className="pipeline-field pipeline-field--wide">
                    <span className="pipeline-field__label-row">
                      Name
                      <span className="pipeline-field__counter">
                        {form.name.length}/{PIPELINE_NAME_MAX_LEN}
                      </span>
                    </span>
                    <input
                      type="text"
                      value={form.name}
                      maxLength={PIPELINE_NAME_MAX_LEN}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          name: e.target.value.slice(0, PIPELINE_NAME_MAX_LEN),
                        }))
                      }
                      placeholder="My automation pipeline"
                    />
                  </label>
                  <label className="pipeline-field">
                    <span>Schedule</span>
                    <select
                      value={form.schedule_type || ''}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          schedule_type: e.target.value
                            ? (e.target.value as PipelineScheduleType)
                            : null,
                        }))
                      }
                    >
                      <option value="">Manual only</option>
                      {SCHEDULE_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="pipeline-field pipeline-field--switch">
                    <PipelineToggleSwitch
                      checked={Boolean(form.enabled)}
                      onChange={(enabled) => setForm((prev) => ({ ...prev, enabled }))}
                      label="Pipeline enabled"
                    />
                  </div>
                </div>
              </section>

              <section className="pipeline-form-section">
                <div className="pipeline-form-section__head">
                  <h4>Pipeline template</h4>
                  <p>
                    Task order is fixed per template. Steps run top to bottom — do not reorder manually.
                  </p>
                </div>
                <div className="pipeline-template-row" role="tablist" aria-label="Pipeline template">
                  {TASK_TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      role="tab"
                      aria-selected={selectedTemplateId === template.id}
                      className={`pipeline-template-chip${
                        selectedTemplateId === template.id ? ' pipeline-template-chip--active' : ''
                      }`}
                      onClick={() => applyTemplate(template.id, template.types)}
                    >
                      {template.label}
                    </button>
                  ))}
                </div>
              </section>

              <section className="pipeline-form-section">
                <div className="pipeline-form-section__head">
                  <h4>Task configuration ({form.tasks.length})</h4>
                  <p>Configure each step before saving. Tuning steps use the integration configurator.</p>
                </div>
                {form.tasks.length === 0 ? (
                  <div className="pipeline-flow-empty">
                    Choose a template above to add steps in the correct order.
                  </div>
                ) : (
                  <div className="pipeline-task-config-list">
                    {form.tasks.map((task, index) => (
                      <PipelineTaskConfigEditor
                        key={`${normalizePipelineTaskType(task.type)}-${index}`}
                        task={task}
                        taskIndex={index}
                        onChange={(next) => updateTask(index, next)}
                        integrationId={integrationId!}
                        virtualUsers={virtualUsers}
                        connectorDatasets={connectorDatasets}
                        connectorDatasetsLoading={connectorDatasetsLoading}
                        configFromConfigurator={configFromConfigurator}
                        optimizationPolicyUi={optimizationPolicyUi}
                        onOptimizationPolicyUiChange={onOptimizationPolicyUiChange}
                        optimizationIterations={optimizationIterations}
                        onOptimizationIterationsChange={onOptimizationIterationsChange}
                        hasSelectTrainingStep={hasSelectTrainingInForm}
                      />
                    ))}
                  </div>
                )}
              </section>
            </div>

            <div className="pipeline-modal__footer">
              <button type="button" className="pipeline-btn pipeline-btn--ghost" onClick={closeForm}>
                Cancel
              </button>
              <button
                type="button"
                className="pipeline-btn pipeline-btn--primary"
                disabled={formLoading}
                onClick={() => void handleSave()}
              >
                {formLoading ? 'Saving…' : editingPipelineId ? 'Save changes' : 'Create pipeline'}
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="pipeline-list-section" id="pipeline-panel-list">
        <div className="pipeline-list-section__head">
          <h3>Your pipelines</h3>
          <span className="pipeline-list-section__count">{filteredPipelines.length}</span>
        </div>

        {loading && filteredPipelines.length === 0 ? (
          <div className="pipeline-empty-state">
            <div className="pipeline-spinner" aria-hidden="true" />
            <p>Loading pipelines…</p>
          </div>
        ) : filteredPipelines.length === 0 ? (
          <div className="pipeline-empty-state">
            <div className="pipeline-empty-state__icon">
              <i className="fas fa-stream" aria-hidden="true" />
            </div>
            <h3>No pipelines yet</h3>
            <p>Create your first automation flow for this integration.</p>
            <button type="button" className="pipeline-btn pipeline-btn--primary" onClick={() => openCreateForm()}>
              Create full cycle
            </button>
          </div>
        ) : (
          <div className="pipeline-grid">
            {filteredPipelines.map((pipeline) => {
              const isToggling = togglingEnabledId === pipeline.id;
              const enabled = Boolean(pipeline.enabled);
              const lastRunStatus = resolvePipelineCardLastRunStatus(
                pipeline,
                activeRun,
                activeRunPipelineIdRef.current
              );
              const canCancelLastRun = Boolean(
                pipeline.last_run_id && canCancelPipelineRun(lastRunStatus)
              );
              const showLastRunError = Boolean(
                pipeline.last_error &&
                  lastRunStatus &&
                  String(lastRunStatus).toLowerCase() === 'failed'
              );
              const isViewingRun = isPipelineRunPanelOpenForPipeline(pipeline, {
                showActiveRunPanel,
                viewedRun,
                activeRun,
                viewedRunPipelineId,
                activeRunPipelineId: activeRunPipelineIdRef.current,
              });
              const isLoadingViewRun = loadingViewRunPipelineId === pipeline.id;
              const viewRunButtonLabel = isLoadingViewRun
                ? 'Loading…'
                : isViewingRun
                  ? 'Hide run'
                  : 'View run';
              return (
                <article
                  key={pipeline.id}
                  className={`pipeline-card ${enabled ? 'pipeline-card--active' : ''}${isViewingRun ? ' pipeline-card--run-panel-open' : ''}`}
                >
                  <div className="pipeline-card__content">
                    <div className="pipeline-card__top">
                      <div className="pipeline-card__title-block">
                        <PipelineToggleSwitch
                          checked={enabled}
                          disabled={isToggling}
                          onChange={() => void handleToggleEnabled(pipeline)}
                          label=""
                        />
                        <div>
                          <h4 className="pipeline-card__name" title={pipeline.name}>
                            {pipeline.name}
                          </h4>
                          <div className="pipeline-card__chips">
                            {pipeline.schedule_type ? (
                              <span className="pipeline-chip">
                                <i className="fas fa-clock" aria-hidden="true" /> {pipeline.schedule_type}
                              </span>
                            ) : (
                              <span className="pipeline-chip pipeline-chip--muted">Manual</span>
                            )}
                            {lastRunStatus && (
                              <span
                                className="pipeline-chip"
                                style={{ color: pipelineStatusColor(lastRunStatus) }}
                              >
                                {lastRunStatus}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="pipeline-card__stats">
                      <div>
                        <span>Next run</span>
                        <strong>{formatDate(pipeline.next_run_at)}</strong>
                      </div>
                      <div>
                        <span>Last run</span>
                        <strong>{formatDate(pipeline.last_run_at)}</strong>
                      </div>
                      <div>
                        <span>Tasks</span>
                        <strong>{pipeline.tasks?.length || '—'}</strong>
                      </div>
                    </div>

                    {showLastRunError ? (
                      <div className="pipeline-card__error" role="alert">
                        <i className="fas fa-exclamation-triangle pipeline-card__error-icon" aria-hidden="true" />
                        <FailedJobErrorMessage
                          rawError={pipeline.last_error}
                          friendlyMessage={formatPipelineErrorMessage(
                            pipeline.last_error,
                            'The last pipeline run failed. Review the configuration and try again.'
                          )}
                          fallbackMessage="The last pipeline run failed. Review the configuration and try again."
                          compact
                        />
                      </div>
                    ) : null}
                  </div>

                  <div className="pipeline-card__actions">
                    <button
                      type="button"
                      className="pipeline-btn pipeline-btn--primary pipeline-btn--small"
                      disabled={runningPipelineId === pipeline.id}
                      onClick={() => void handleRun(pipeline)}
                    >
                      <i className="fas fa-play" aria-hidden="true" />
                      {runningPipelineId === pipeline.id ? 'Starting…' : 'Run now'}
                    </button>
                    <button
                      type="button"
                      className="pipeline-btn pipeline-btn--ghost pipeline-btn--small"
                      onClick={() => void openEditForm(pipeline.id)}
                    >
                      <i className="fas fa-pen" aria-hidden="true" />
                      Edit
                    </button>
                    {pipeline.last_run_id ? (
                      <button
                        type="button"
                        className={`pipeline-btn pipeline-btn--ghost pipeline-btn--small${isViewingRun ? ' pipeline-btn--viewing-run' : ''}`}
                        aria-pressed={isViewingRun}
                        aria-busy={isLoadingViewRun}
                        disabled={isLoadingViewRun}
                        title={
                          isViewingRun
                            ? 'Hide run details panel above'
                            : 'Show last run steps, progress, and errors above'
                        }
                        onClick={() => void handleViewRun(pipeline)}
                      >
                        <i
                          className={`fas ${isLoadingViewRun ? 'fa-spinner fa-spin' : isViewingRun ? 'fa-eye-slash' : 'fa-eye'}`}
                          aria-hidden="true"
                        />
                        {viewRunButtonLabel}
                      </button>
                    ) : (
                      <span className="pipeline-card__action-slot" aria-hidden="true" />
                    )}
                    {canCancelLastRun ? (
                      <button
                        type="button"
                        className="pipeline-btn pipeline-btn--danger pipeline-btn--small"
                        disabled={Boolean(cancellingRunId)}
                        aria-busy={cancellingRunId === pipeline.last_run_id}
                        onClick={() => void handleCancelRun(pipeline.last_run_id!)}
                      >
                        <i
                          className={`fas ${cancellingRunId === pipeline.last_run_id ? 'fa-spinner fa-spin' : 'fa-ban'}`}
                          aria-hidden="true"
                        />
                        {cancellingRunId === pipeline.last_run_id ? 'Cancelling…' : 'Cancel run'}
                      </button>
                    ) : (
                      <span className="pipeline-card__action-slot" aria-hidden="true" />
                    )}
                    <button
                      type="button"
                      className="pipeline-btn pipeline-btn--danger pipeline-btn--small pipeline-card__action-delete"
                      onClick={() => setDeleteConfirm({ isOpen: true, pipelineId: pipeline.id })}
                    >
                      <i className="fas fa-trash-alt" aria-hidden="true" />
                      Delete
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        title="Delete pipeline?"
        message="This will permanently delete the pipeline and its schedule."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteConfirm({ isOpen: false, pipelineId: null })}
        isConfirmLoading={deleteLoading}
        variant="danger"
      />
    </div>
  );
};

export default PipelinePanel;
